"use client";
import { useCallback, useMemo, useState } from "react";
import {
  Droplets,
  Wind,
  Gauge,
  Thermometer,
  Filter,
  AlertTriangle,
  ExternalLink,
  ShieldAlert,
  Timer,
  DollarSign,
  Flame,
  Zap,
  ArrowRightLeft,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Pencil,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AnimateOnScroll } from "@/components/home/animate-on-scroll";
import { CalcSettingsProvider, useCalcSettings } from "@/components/calculators/settings-context";
import { CalcSettingsPanel } from "@/components/calculators/settings-panel";
import { EmailResultsModal, type EmailSummary } from "@/components/calculators/email-results-modal";
import { WeatherBanner } from "@/components/calculators/weather-banner";
import { RoomProvider, useRooms, type AffectedSurface, type MaterialType, type Room } from "@/components/calculators/room-context";
import {
  toNum, fmt, DEHU_CATALOG, DEHU_CLASS_FACTOR, NAM_UNITS,
  type DehuModel,
} from "@/components/calculators/calc-constants";
import { NumField, ResultPanel, BigStat, ZoneBlock, EmailButton } from "@/components/calculators/calc-ui";
import { WaterDamageTab } from "./tabs/water-damage";
import { MoldRemediationTab } from "./tabs/mold-remediation";
import { FireSmokeTab } from "./tabs/fire-smoke";
import { ReconstructionTab } from "./tabs/reconstruction";
import { GeneralToolsTab } from "./tabs/general-tools";

/* DEHU_CATALOG, DEHU_CLASS_FACTOR, NAM_UNITS, toNum, fmt
   are imported from @/components/calculators/calc-constants above.
   NumField, ResultPanel, BigStat, ZoneBlock, EmailButton
   are imported from @/components/calculators/calc-ui above.            */
type AirMoverModel = { id: string; label: string; cfm: number; amps: number; type: "centrifugal" | "axial" };
const AIR_MOVER_CATALOG: AirMoverModel[] = [
  { id: "dri-eaz-ace-turbodryer",   label: "Dri-Eaz Ace TurboDryer",   cfm: 3200, amps: 2.1, type: "centrifugal" },
  { id: "dri-eaz-sahara-pro-x2",    label: "Dri-Eaz Sahara Pro X2",    cfm: 3100, amps: 1.8, type: "centrifugal" },
  { id: "dri-eaz-velo-low-profile", label: "Dri-Eaz Velo Pro",         cfm: 2500, amps: 1.3, type: "centrifugal" },
  { id: "dri-eaz-studebaker",       label: "Dri-Eaz Studebaker",       cfm: 3400, amps: 2.9, type: "axial" },
  { id: "dri-eaz-axial-x8",         label: "Dri-Eaz X8 Axial",         cfm: 2800, amps: 2.0, type: "axial" },
  { id: "phoenix-axial-ax",         label: "Phoenix Axial AX",         cfm: 2950, amps: 2.3, type: "axial" },
  { id: "phoenix-air-max",          label: "Phoenix Air Max",          cfm: 3350, amps: 2.8, type: "centrifugal" },
  { id: "bair-vp-25",               label: "B-Air VP-25",              cfm: 2500, amps: 1.5, type: "centrifugal" },
  { id: "bair-vp-33",               label: "B-Air VP-33",              cfm: 3300, amps: 2.3, type: "centrifugal" },
  { id: "xpower-p-630",             label: "XPOWER P-630",             cfm: 2800, amps: 2.0, type: "centrifugal" },
  { id: "xpower-x-830",             label: "XPOWER X-830",             cfm: 3600, amps: 2.8, type: "centrifugal" },
  { id: "generic-snail",            label: "Generic snail (budget)",   cfm: 2400, amps: 1.6, type: "centrifugal" },
];

/* -------------------------------------------------------------------------- */
/*  Tabs config                                                                */
/* -------------------------------------------------------------------------- */
type TabId = "water" | "mold" | "fire" | "reconstruction" | "general";
const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "water",          label: "Water Damage",    icon: "💧" },
  { id: "mold",           label: "Mold Remediation", icon: "🛡" },
  { id: "fire",           label: "Fire & Smoke",    icon: "🔥" },
  { id: "reconstruction", label: "Reconstruction",  icon: "🏗" },
  { id: "general",        label: "General Tools",   icon: "🔧" },
];

/* -------------------------------------------------------------------------- */
/*  How To Use                                                                 */
/* -------------------------------------------------------------------------- */
function HowToUse() {
  const steps = [
    {
      num: "1",
      icon: "🏠",
      title: "Add Your Rooms",
      body: `Click "Add Room" in the Job Setup panel. Enter each affected room's dimensions (length × width × height), which surfaces are wet (floor, walls, ceiling), the material type, and water height if standing water is present. Add every room — the calculators combine them automatically.`,
    },
    {
      num: "2",
      icon: "📋",
      title: "Pick the Right Tab",
      body: "Select the tab that matches the job type: Water Damage for drying equipment sizing, Mold Remediation for containment and ACH, Fire & Smoke for ozone/hydroxyl and air scrubbers, Reconstruction for cost estimation, or General Tools for psychrometrics and power load.",
    },
    {
      num: "3",
      icon: "⚙️",
      title: "Adjust the Parameters",
      body: "Each tab pre-fills from your room data. Override any field — water source, class of loss, dehu model, NAM CFM — to match what you have on the truck or what the job actually requires. The results update instantly.",
    },
    {
      num: "4",
      icon: "📧",
      title: "Email the Results",
      body: `Hit "Email Results" at the bottom of any calculator to send a formatted summary to yourself, the adjuster, or the property owner. Use it as a quick scope document or leave-behind for the insured.`,
    },
  ];

  const whyItems = [
    { label: "Defensible scope", text: "Numbers backed by IICRC S500 / S520 are harder for adjusters to dispute. Show your math." },
    { label: "Faster mobilization", text: "Calculate equipment needs on the way to the job — not after you arrive." },
    { label: "Avoid under-equiping", text: "The #1 cause of failed drying goals is too few air movers or wrong dehu class. These tools catch that upfront." },
    { label: "Contents documentation", text: "The Fire & Smoke tab's inventory separates total-loss, salvageable, and cleanable items for the adjuster — in the field, not back at the office." },
    { label: "Training tool", text: "Newer techs can use the reference guide and calculator together to learn the IICRC standard as they work." },
  ];

  return (
    <section style={{ padding: "0 24px 32px", position: "relative", zIndex: 1 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 20, padding: "28px 32px", boxShadow: "0 4px 32px rgba(0,0,0,0.6)" }}>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 20 }}>📖</span>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>How to Use These Calculators</h2>
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.60)", marginBottom: 24, lineHeight: 1.6 }}>
            These are field-ready engineering tools built to the IICRC S500 / S520 standards. Follow these four steps on every job.
          </p>

          {/* Steps */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 28 }}>
            {steps.map((s) => (
              <div key={s.num} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, padding: "18px 18px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#B88A2F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#080F1A", flexShrink: 0 }}>{s.num}</div>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>
                  <p style={{ fontSize: 14, fontWeight: 700, color: "#ffffff" }}>{s.title}</p>
                </div>
                <p style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", lineHeight: 1.65 }}>{s.body}</p>
              </div>
            ))}
          </div>

          {/* Why it matters */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", paddingTop: 20 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Why This Matters on Every Job</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {whyItems.map((item) => (
                <div key={item.label} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#D4A84B", whiteSpace: "nowrap", marginTop: 1, minWidth: 160 }}>{item.label}</span>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", lineHeight: 1.6 }}>{item.text}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Room tile add form                                                         */
/* -------------------------------------------------------------------------- */
const BLANK_ROOM_FORM = () => ({
  name: "",
  length: "",
  width: "",
  height: "8",
  affectedSurfaces: ["floor"] as AffectedSurface[],
  waterHeightIn: "",
  materialType: "drywall" as MaterialType,
});

function RoomSetup() {
  const { rooms, addRoom, updateRoom, removeRoom, summary, hasRooms } = useRooms();
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK_ROOM_FORM());
  const [editingId, setEditingId] = useState<string | null>(null);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const toggleSurface = (s: AffectedSurface) => {
    setField("affectedSurfaces", form.affectedSurfaces.includes(s)
      ? form.affectedSurfaces.filter((x) => x !== s)
      : [...form.affectedSurfaces, s]);
  };

  const handleSubmit = () => {
    const L = toNum(form.length), W = toNum(form.width), H = toNum(form.height);
    if (!L || !W || !H) return;
    const roomData = {
      name: form.name || `Room ${rooms.length + 1}`,
      length: L,
      width: W,
      height: H,
      affectedSurfaces: form.affectedSurfaces.length ? form.affectedSurfaces : ["floor" as AffectedSurface],
      waterHeightIn: toNum(form.waterHeightIn),
      materialType: form.materialType,
    };
    if (editingId) {
      updateRoom(editingId, roomData);
      setEditingId(null);
    } else {
      addRoom(roomData);
    }
    setForm(BLANK_ROOM_FORM());
    setFormOpen(false);
  };

  const startEdit = (room: Room) => {
    setForm({
      name: room.name,
      length: String(room.length),
      width: String(room.width),
      height: String(room.height),
      affectedSurfaces: room.affectedSurfaces,
      waterHeightIn: room.waterHeightIn ? String(room.waterHeightIn) : "",
      materialType: room.materialType,
    });
    setEditingId(room.id);
    setFormOpen(true);
  };

  const surfaceColors: Record<AffectedSurface, string> = {
    floor: "#60a5fa",
    walls: "#a78bfa",
    ceiling: "#4ade80",
  };

  return (
    <section style={{ padding: "0 24px 32px", position: "relative", zIndex: 1 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* Panel wrapper — gives the section a visible dark background */}
        <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 20, padding: "24px 28px", boxShadow: "0 4px 32px rgba(0,0,0,0.6)" }}>

        {/* Section header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#ffffff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Step 1 — Job Setup</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.60)", marginTop: 3 }}>Add each affected room or area. All calculators below will pre-fill from this data.</p>
          </div>
          <button
            type="button"
            onClick={() => { setEditingId(null); setForm(BLANK_ROOM_FORM()); setFormOpen((prev) => !prev); }}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 100, border: "1px solid #D4A84B", background: "rgba(212,168,75,0.15)", color: "#D4A84B", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
          >
            <Plus size={15} /> Add Room
          </button>
        </div>

        {/* Room tiles */}
        {hasRooms && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" style={{ marginBottom: 18 }}>
            {rooms.map((room) => (
              <div key={room.id} style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "16px 18px", position: "relative" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, color: "#D4A84B", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{room.name}</p>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" onClick={() => startEdit(room)} aria-label="Edit room" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", borderRadius: 6, color: "rgba(255,255,255,0.7)", padding: "4px 7px", cursor: "pointer", fontSize: 12 }}>
                      <Pencil size={12} />
                    </button>
                    <button type="button" onClick={() => removeRoom(room.id)} aria-label="Remove room" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", borderRadius: 6, color: "rgba(255,255,255,0.7)", padding: "4px 7px", cursor: "pointer", fontSize: 12 }}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.70)", marginBottom: 6 }}>{room.length} × {room.width} × {room.height} ft · {fmt(room.length * room.width)} ft²</p>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
                  {room.affectedSurfaces.map((s) => (
                    <span key={s} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 100, background: surfaceColors[s] + "22", border: `1px solid ${surfaceColors[s]}55`, color: surfaceColors[s], textTransform: "capitalize" }}>{s}</span>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.50)", textTransform: "capitalize" }}>{room.materialType}{room.waterHeightIn ? ` · ${room.waterHeightIn}" water` : ""}</p>
              </div>
            ))}
          </div>
        )}

        {/* Add / edit form */}
        {formOpen && (
          <div style={{ background: "rgba(13,25,41,0.92)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "22px 24px", marginBottom: 18 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", marginBottom: 16 }}>{editingId ? "Edit Room" : "New Room"}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5" style={{ gridColumn: "1 / -1" }}>
                <Label htmlFor="rs-name" style={{ color: "rgba(255,255,255,0.85)" }}>Room name</Label>
                <Input id="rs-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder={`Room ${rooms.length + 1}`} />
              </div>
              <NumField id="rs-length" label="Length (ft)" value={form.length} onChange={(v) => setField("length", v)} placeholder="12" />
              <NumField id="rs-width" label="Width (ft)" value={form.width} onChange={(v) => setField("width", v)} placeholder="14" />
              <NumField id="rs-height" label="Height (ft)" value={form.height} onChange={(v) => setField("height", v)} placeholder="8" />
              <div>
                <Label style={{ color: "rgba(255,255,255,0.85)", marginBottom: 8, display: "block" }}>Affected surfaces</Label>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["floor", "walls", "ceiling"] as AffectedSurface[]).map((s) => (
                    <button key={s} type="button" onClick={() => toggleSurface(s)}
                      style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid " + (form.affectedSurfaces.includes(s) ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: form.affectedSurfaces.includes(s) ? "rgba(212,168,75,0.2)" : "transparent", color: form.affectedSurfaces.includes(s) ? "#D4A84B" : "rgba(255,255,255,0.75)", textTransform: "capitalize" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <NumField id="rs-water" label="Water height (in, optional)" value={form.waterHeightIn} onChange={(v) => setField("waterHeightIn", v)} placeholder="0" />
              <div className="space-y-1.5">
                <Label htmlFor="rs-material" style={{ color: "rgba(255,255,255,0.85)" }}>Material type</Label>
                <Select value={form.materialType} onValueChange={(v) => setField("materialType", v as MaterialType)}>
                  <SelectTrigger id="rs-material" className="h-10 w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drywall">Drywall</SelectItem>
                    <SelectItem value="plaster">Plaster</SelectItem>
                    <SelectItem value="hardwood">Hardwood</SelectItem>
                    <SelectItem value="tile">Tile</SelectItem>
                    <SelectItem value="carpet">Carpet</SelectItem>
                    <SelectItem value="concrete">Concrete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button type="button" onClick={handleSubmit} style={{ padding: "10px 22px", borderRadius: 100, border: "1px solid #D4A84B", background: "#D4A84B", color: "#080F1A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {editingId ? "Save Changes" : "Add Room"}
              </button>
              <button type="button" onClick={() => { setFormOpen(false); setEditingId(null); setForm(BLANK_ROOM_FORM()); }} style={{ padding: "10px 18px", borderRadius: 100, border: "1px solid rgba(255,255,255,0.20)", background: "transparent", color: "rgba(255,255,255,0.75)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Aggregated summary bar */}
        {hasRooms && (
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "14px 20px", display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums" }}>
              <strong style={{ color: "#D4A84B" }}>{fmt(summary.totalFloorSqFt)}</strong> ft² floor
            </span>
            <span style={{ color: "rgba(255,255,255,0.35)" }}>|</span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums" }}>
              <strong style={{ color: "#D4A84B" }}>{fmt(summary.totalVolumeFt3)}</strong> ft³ volume
            </span>
            {summary.totalStandingGal > 0 && (
              <>
                <span style={{ color: "rgba(255,255,255,0.35)" }}>|</span>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums" }}>
                  Standing water: <strong style={{ color: "#60a5fa" }}>{fmt(summary.totalStandingGal, 1)} gal</strong>
                </span>
              </>
            )}
            <span style={{ color: "rgba(255,255,255,0.35)" }}>|</span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
              <strong style={{ color: "#D4A84B" }}>{rooms.length}</strong> {rooms.length === 1 ? "room" : "rooms"}
            </span>
          </div>
        )}

        {/* Empty state nudge */}
        {!hasRooms && !formOpen && (
          <div style={{ textAlign: "center", padding: "24px 0 8px" }}>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.50)" }}>No rooms added yet — click <strong style={{ color: "#D4A84B" }}>Add Room</strong> above to get started.</p>
          </div>
        )}

        </div>{/* end panel wrapper */}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tab bar + tab content                                                      */
/* -------------------------------------------------------------------------- */
function TabsSection() {
  const [activeTab, setActiveTab] = useState<TabId>("water");

  return (
    <section style={{ padding: "0 24px 48px", position: "relative", zIndex: 1 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 20, boxShadow: "0 4px 32px rgba(0,0,0,0.6)", overflow: "hidden" }}>

          {/* Step 2 label */}
          <div style={{ padding: "20px 28px 0" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Step 2 — Select Calculator</p>
          </div>

          {/* Tab bar */}
          <div style={{ padding: "0 28px 20px", borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: "10px 22px",
                    borderRadius: 100,
                    border: activeTab === tab.id ? "1px solid #D4A84B" : "1px solid rgba(255,255,255,0.18)",
                    background: activeTab === tab.id ? "rgba(212,168,75,0.18)" : "rgba(255,255,255,0.04)",
                    color: activeTab === tab.id ? "#D4A84B" : "rgba(255,255,255,0.75)",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content.

              This used to be hidden behind `hasRooms`, so a visitor landing
              on /authority/calculators saw one empty box reading "Add at
              least one room to get started" and nothing else — a page that
              looks broken, on a term we are trying to rank for. The gate is
              gone; every tab now accepts manual dimensions and works on
              arrival, pre-filling from Job Setup when rooms exist. */}
          <div style={{ padding: "28px 28px 32px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {activeTab === "water"          && <WaterDamageTab />}
              {activeTab === "mold"           && <MoldRemediationTab />}
              {activeTab === "fire"           && <FireSmokeTab />}
              {activeTab === "reconstruction" && <ReconstructionTab />}
              {activeTab === "general"        && <GeneralToolsTab />}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function CalculatorsPage() {
  return (
    <CalcSettingsProvider>
      <RoomProvider>
        <div style={{ minHeight: "100vh", backgroundImage: "url('/images/arizona-landmarks/grand-canyon-view-arizona.jpg')", backgroundSize: "cover", backgroundAttachment: "fixed", backgroundPosition: "center" }}>
          {/* Header */}
          <section style={{ padding: "120px 24px 80px", background: "rgba(5,13,24,0.50)", textAlign: "center", position: "relative", zIndex: 1 }}>
            <AnimateOnScroll>
              <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "56px 48px" }}>
                <div className="h-[3px] w-24 bg-[#D4A84B]" style={{ display: "inline-block" }} />
                <p className="mt-6 text-sm font-semibold uppercase tracking-widest text-center" style={{ color: "#D4A84B" }}>
                  Restoration Industry Resources
                </p>
                <h1 className="mt-2 text-4xl font-bold tracking-tight md:text-5xl" style={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Restoration Calculators
                </h1>
                <p className="mt-3 text-lg text-center" style={{ color: "rgba(255,255,255,0.82)", lineHeight: "1.75", maxWidth: "42rem", marginLeft: "auto", marginRight: "auto" }}>
                  Field-ready engineering tools aligned to IICRC&nbsp;S500 guidance. Enter your
                  loss parameters and let the math do the arguing. Designed for mobile —
                  every input opens a numeric keypad.
                </p>
              </div>
            </AnimateOnScroll>
          </section>

          <WeatherBanner />
          <CalcSettingsPanel />
          <HowToUse />
          <FieldReferenceGuide />
          <RoomSetup />
          <TabsSection />

          {/* Disclaimer */}
          <section style={{ padding: "0 24px 72px", background: "rgba(5,13,24,0.50)", position: "relative", zIndex: 1 }}>
            <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(220,38,38,0.3)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "28px 48px" }}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[#D4A84B]" />
                <div className="text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>
                  <p className="font-semibold" style={{ color: "#ffffff" }}>IICRC S500 Disclaimer</p>
                  <p className="mt-1 max-w-4xl leading-relaxed">
                    These calculators are simplified engineering aids derived from the{" "}
                    <em>ANSI/IICRC S500 Standard for Professional Water Damage Restoration</em>
                    {" "}and related air-quality guidance. They do not replace on-site
                    assessment by a certified restoration professional. Actual equipment
                    needs depend on psychrometric conditions, building materials, contamination
                    category, containment, and airflow obstructions. Always defer to
                    manufacturer specifications and the current IICRC standards.{" "}
                    <a
                      href="https://iicrc.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-semibold underline underline-offset-2"
                      style={{ color: "#D4A84B" }}
                    >
                      Reference the IICRC standards at iicrc.org
                      <ExternalLink className="size-3" />
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </RoomProvider>
    </CalcSettingsProvider>
  );
}

/* ─── Legacy calculator components (unreachable; kept to avoid structural churn) ─── */
function AirMoverCalc() {
  const [floorL, setFloorL] = useState("");
  const [floorW, setFloorW] = useState("");
  const [ceilL, setCeilL] = useState("");
  const [ceilW, setCeilW] = useState("");
  const [wallL, setWallL] = useState("");
  const [wallH, setWallH] = useState("");
  const [insets, setInsets] = useState("");

  const result = useMemo(() => {
    const fA = toNum(floorL) * toNum(floorW);
    const cA = toNum(ceilL) * toNum(ceilW);
    const wH = Math.max(0, toNum(wallH) - 2);
    const wA = toNum(wallL) * wH;
    const ins = Math.max(0, Math.floor(toNum(insets)));
    const min = Math.ceil(fA / 70) + Math.ceil(cA / 150) + Math.ceil(wA / 150) + ins;
    const max = Math.ceil(fA / 50) + Math.ceil(cA / 100) + Math.ceil(wA / 100) + ins;
    return { fA, cA, wA, ins, min, max };
  }, [floorL, floorW, ceilL, ceilW, wallL, wallH, insets]);

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="flex flex-col gap-6">
        <ZoneBlock title="Floor" rule="1 air mover per 50–70 sq ft of wet floor"
          a={<NumField id="am-floor-l" label="Length (ft)" value={floorL} onChange={setFloorL} placeholder="24" />}
          b={<NumField id="am-floor-w" label="Width (ft)" value={floorW} onChange={setFloorW} placeholder="18" />}
          metric={`${fmt(result.fA)} wet ft²`} />
        <ZoneBlock title="Ceiling" rule="1 air mover per 100–150 sq ft of wet ceiling"
          a={<NumField id="am-ceil-l" label="Length (ft)" value={ceilL} onChange={setCeilL} placeholder="24" />}
          b={<NumField id="am-ceil-w" label="Width (ft)" value={ceilW} onChange={setCeilW} placeholder="18" />}
          metric={`${fmt(result.cA)} wet ft²`} />
        <ZoneBlock title="Walls" rule="1 air mover per 100–150 sq ft of wet wall above 2 ft"
          a={<NumField id="am-wall-l" label="Total wall length (ft)" value={wallL} onChange={setWallL} placeholder="64" />}
          b={<NumField id="am-wall-h" label="Wet height (ft)" value={wallH} onChange={setWallH} placeholder="6" />}
          metric={`${fmt(result.wA)} wet ft² (above 2 ft)`} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
          <NumField id="am-insets" label="# of insets / offsets > 18″" value={insets} onChange={setInsets} placeholder="0" />
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", lineHeight: 1.5 }}>+1 air mover per qualifying inset or offset.</p>
        </div>
      </div>
      <div>
        <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#D4A84B" }}>Air Movers Required</p>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginTop: 6 }}>
              <div><span style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Min</span><p style={{ fontSize: 44, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{result.min || "—"}</p></div>
              <span style={{ fontSize: 24, color: "rgba(255,255,255,0.45)" }}>→</span>
              <div><span style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Max</span><p style={{ fontSize: 44, fontWeight: 700, color: "#D4A84B", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{result.max || "—"}</p></div>
            </div>
          </div>
          <dl style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 12, fontSize: 13 }}>
            <dt style={{ color: "rgba(255,255,255,0.7)" }}>Floor contribution</dt><dd style={statStyle}>{Math.ceil(result.fA / 70)}–{Math.ceil(result.fA / 50)}</dd>
            <dt style={{ color: "rgba(255,255,255,0.7)" }}>Ceiling contribution</dt><dd style={statStyle}>{Math.ceil(result.cA / 150)}–{Math.ceil(result.cA / 100)}</dd>
            <dt style={{ color: "rgba(255,255,255,0.7)" }}>Wall contribution</dt><dd style={statStyle}>{Math.ceil(result.wA / 150)}–{Math.ceil(result.wA / 100)}</dd>
            <dt style={{ color: "rgba(255,255,255,0.7)" }}>Insets / offsets</dt><dd style={statStyle}>{result.ins}</dd>
          </dl>
          <p style={{ padding: "10px 20px 16px", color: "rgba(255,255,255,0.70)", fontSize: 11, lineHeight: 1.55, borderTop: "1px solid rgba(255,255,255,0.08)" }}>Per IICRC S500 (2015). The Min count is the conservative low end; Max is the aggressive high end. Round up.</p>
          <div style={{ padding: "0 20px 20px" }}>
            <EmailButton summary={{ calcLabel: "Air Movers (S500 2015)", rows: [{ label: "Air Movers Min", value: String(result.min) }, { label: "Air Movers Max", value: String(result.max) }] }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DehuCalc() {
  const { settings } = useCalcSettings();
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState(String(settings.defaultHeightFt || 8));
  const [coverage, setCoverage] = useState("100");
  const [waterHeightIn, setWaterHeightIn] = useState("");
  const [classOfLoss, setClassOfLoss] = useState("2");
  const [dehuId, setDehuId] = useState(settings.defaultDehuId);
  const dehu = DEHU_CATALOG.find((d) => d.id === dehuId) ?? DEHU_CATALOG[0];
  const r = useMemo(() => {
    const L = toNum(length), W = toNum(width), H = toNum(height);
    const cov = (toNum(coverage) || 100) / 100;
    const floorArea = L * W * cov;
    const volume = floorArea * H;
    const standingGal = floorArea * toNum(waterHeightIn) * 0.62338;
    const factor = DEHU_CLASS_FACTOR[classOfLoss] ?? 0.2;
    const pintsPerDay = volume * factor;
    const dehuCount = dehu.aham > 0 && pintsPerDay > 0 ? Math.ceil(pintsPerDay / dehu.aham) : 0;
    const perimeter = 2 * (L + W) * cov;
    const airMoversByArea = floorArea > 0 ? Math.ceil(floorArea / 60) : 0;
    const airMoversByLinear = perimeter > 0 ? Math.ceil(perimeter / 13) : 0;
    const dehuAmps = dehuCount * dehu.amps;
    return { floorArea, volume, standingGal, pintsPerDay, dehuCount, airMoversByArea, airMoversByLinear, dehuAmps, perimeter };
  }, [length, width, height, coverage, waterHeightIn, classOfLoss, dehu]);
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="flex flex-col gap-5">
        <div>
          <Label style={{ color: "rgba(255,255,255,0.85)", marginBottom: 6, display: "block" }}>Class of Loss</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {["1", "2", "3", "4"].map((c) => (
              <button key={c} type="button" onClick={() => setClassOfLoss(c)} aria-pressed={classOfLoss === c} style={{ padding: "10px 8px", borderRadius: 8, border: "1px solid " + (classOfLoss === c ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: classOfLoss === c ? "#B88A2F" : "transparent", color: classOfLoss === c ? "#080F1A" : "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Class {c}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <NumField id="dh-length" label="Length (ft)" value={length} onChange={setLength} placeholder="24" />
          <NumField id="dh-width" label="Width (ft)" value={width} onChange={setWidth} placeholder="18" />
          <NumField id="dh-height" label="Height (ft)" value={height} onChange={setHeight} placeholder="8" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField id="dh-coverage" label="% Coverage wet" value={coverage} onChange={setCoverage} placeholder="100" />
          <NumField id="dh-water-in" label="Water height (in)" value={waterHeightIn} onChange={setWaterHeightIn} placeholder="0" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dh-unit">Dehumidifier</Label>
          <Select value={dehuId || "none"} onValueChange={(v) => setDehuId(v === "none" ? "" : v ?? "")}>
            <SelectTrigger id="dh-unit" className="h-10 w-full"><SelectValue placeholder="Select a dehumidifier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None — clear selection</SelectItem>
              {DEHU_CATALOG.map((u) => <SelectItem key={u.id} value={u.id}>{u.label} — {u.aham} pts / {u.amps}A</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <BigStat label="Dehumidifiers" stat={r.dehuCount || "—"} sub={r.dehuCount ? dehu.label : ""} highlight />
          <BigStat label="AHAM needed" stat={r.pintsPerDay ? fmt(r.pintsPerDay) : "—"} sub="pints / day" />
        </div>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <BigStat label="Air Movers by area" stat={r.airMoversByArea || "—"} sub="1 per 50–70 ft²" />
          <BigStat label="Air Movers by linear ft" stat={r.airMoversByLinear || "—"} sub="1 per 10–16 ft" />
        </div>
        <dl style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 12, fontSize: 13 }}>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Floor area</dt><dd style={statStyle}>{r.floorArea ? `${fmt(r.floorArea)} ft²` : "—"}</dd>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Volume</dt><dd style={statStyle}>{r.volume ? `${fmt(r.volume)} ft³` : "—"}</dd>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Standing water</dt><dd style={statStyle}>{r.standingGal ? `${fmt(r.standingGal, 1)} gal` : "—"}</dd>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Perimeter</dt><dd style={statStyle}>{r.perimeter ? `${fmt(r.perimeter)} ft` : "—"}</dd>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Dehu power draw</dt><dd style={statStyle}>{r.dehuAmps ? `${fmt(r.dehuAmps, 1)} A` : "—"}</dd>
        </dl>
        <div style={{ padding: "0 20px 20px" }}>
          <EmailButton summary={{ calcLabel: "Drying Equipment", rows: [{ label: "Class", value: `Class ${classOfLoss}` }, { label: "Dehu count", value: String(r.dehuCount) }, { label: "AHAM needed", value: `${fmt(r.pintsPerDay)} pts/day` }] }} />
        </div>
      </div>
    </div>
  );
}

function MoldContainmentCalc() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("8");
  const [targetAch, setTargetAch] = useState("6");
  const [namCfm, setNamCfm] = useState("500");
  const { volume, requiredCfm, unitsNeeded } = useMemo(() => {
    const L = toNum(length), W = toNum(width), H = toNum(height), ach = toNum(targetAch), cfm = toNum(namCfm);
    const vol = L * W * H;
    const req = (vol * ach) / 60;
    const units = cfm > 0 ? Math.ceil(req / cfm) : 0;
    return { volume: vol, requiredCfm: req, unitsNeeded: units };
  }, [length, width, height, targetAch, namCfm]);
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <NumField id="mc-l" label="Length (ft)" value={length} onChange={setLength} placeholder="e.g. 18" />
          <NumField id="mc-w" label="Width (ft)" value={width} onChange={setWidth} placeholder="e.g. 12" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumField id="mc-h" label="Ceiling height (ft)" value={height} onChange={setHeight} />
          <div className="space-y-1.5">
            <Label htmlFor="mc-ach">Target ACH</Label>
            <Select value={targetAch} onValueChange={(v) => setTargetAch(v ?? "")}>
              <SelectTrigger id="mc-ach" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 ACH — Level II</SelectItem>
                <SelectItem value="6">6 ACH — Level III</SelectItem>
                <SelectItem value="8">8 ACH — Level IV</SelectItem>
                <SelectItem value="12">12 ACH — Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mc-nam">Negative-air machine</Label>
          <Select value={namCfm} onValueChange={(v) => setNamCfm(v ?? "")}>
            <SelectTrigger id="mc-nam" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {NAM_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <ResultPanel title="NAMs Required" stat={unitsNeeded ? String(unitsNeeded) : "—"} unit="units"
        rows={[
          { label: "Containment volume", value: volume ? `${fmt(volume)} ft³` : "—" },
          { label: "Required airflow", value: requiredCfm ? `${fmt(requiredCfm)} CFM` : "—" },
          { label: "Target air changes", value: `${targetAch} ACH` },
        ]}
        footnote="Per IICRC S520 / ANSI Z9.5. Verify with a smoke pencil after setup." />
    </div>
  );
}

const BASELINE_DRY_DAYS: Record<string, number> = { "1": 3, "2": 4, "3": 6, "4": 10 };
function DryingTimeCalc() {
  const [area, setArea] = useState("");
  const [ceilingHeight, setCeilingHeight] = useState("8");
  const [classOfLoss, setClassOfLoss] = useState("2");
  const [installedCapacity, setInstalledCapacity] = useState("");
  const { requiredCapacity, ratio, estDays } = useMemo(() => {
    const A = toNum(area), H = toNum(ceilingHeight), installed = toNum(installedCapacity);
    const factor = DEHU_CLASS_FACTOR[classOfLoss] ?? 0.2;
    const volume = A * H;
    const required = volume * factor;
    const baseline = BASELINE_DRY_DAYS[classOfLoss] ?? 4;
    if (!required || !installed) return { requiredCapacity: required, ratio: 0, estDays: 0 };
    const r = installed / required;
    let days = baseline / r;
    if (r >= 1) days = baseline / Math.min(1.5, Math.sqrt(r));
    return { requiredCapacity: required, ratio: r, estDays: Math.max(1, Math.round(days)) };
  }, [area, ceilingHeight, classOfLoss, installedCapacity]);
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <NumField id="dt-area" label="Floor area (ft²)" value={area} onChange={setArea} placeholder="e.g. 800" />
          <NumField id="dt-h" label="Ceiling height (ft)" value={ceilingHeight} onChange={setCeilingHeight} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dt-class">Class of Loss</Label>
          <Select value={classOfLoss} onValueChange={(v) => setClassOfLoss(v ?? "")}>
            <SelectTrigger id="dt-class" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Class 1</SelectItem>
              <SelectItem value="2">Class 2</SelectItem>
              <SelectItem value="3">Class 3</SelectItem>
              <SelectItem value="4">Class 4 — specialty</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <NumField id="dt-cap" label="Installed dehu capacity (pints / day)" value={installedCapacity} onChange={setInstalledCapacity} placeholder="e.g. 350" />
      </div>
      <ResultPanel title="Estimated Drying" stat={estDays ? String(estDays) : "—"} unit="days"
        rows={[
          { label: "Required capacity", value: requiredCapacity ? `${fmt(requiredCapacity)} pts/day` : "—" },
          { label: "Capacity ratio", value: ratio ? `${fmt(ratio, 2)}×` : "—" },
          { label: "Baseline (this Class)", value: `${BASELINE_DRY_DAYS[classOfLoss]} days` },
        ]}
        footnote="Estimate only. Real drying time depends on psychrometrics, containment, substrate moisture, and daily monitoring." />
    </div>
  );
}

const BASE_RATE_BY_CATEGORY: Record<string, number> = { "1": 3.5, "2": 5.5, "3": 9.0 };
const CLASS_MULT: Record<string, number> = { "1": 1.0, "2": 1.2, "3": 1.5, "4": 2.0 };
function LossEstimatorCalc() {
  const [area, setArea] = useState("");
  const [category, setCategory] = useState("1");
  const [classOfLoss, setClassOfLoss] = useState("2");
  const { low, high } = useMemo(() => {
    const A = toNum(area);
    const rate = BASE_RATE_BY_CATEGORY[category] ?? 3.5;
    const mult = CLASS_MULT[classOfLoss] ?? 1.2;
    const mid = A * rate * mult;
    return { low: Math.round(mid * 0.8), high: Math.round(mid * 1.25) };
  }, [area, category, classOfLoss]);
  const fmtUsd = (n: number) => n ? `$${n.toLocaleString("en-US")}` : "—";
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-5">
        <NumField id="le-area" label="Affected floor area (ft²)" value={area} onChange={setArea} placeholder="e.g. 1200" />
        <div className="space-y-1.5">
          <Label htmlFor="le-cat">Water Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v ?? "")}>
            <SelectTrigger id="le-cat" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Cat 1 — clean water</SelectItem>
              <SelectItem value="2">Cat 2 — gray water</SelectItem>
              <SelectItem value="3">Cat 3 — black water</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="le-class">Class of Loss</Label>
          <Select value={classOfLoss} onValueChange={(v) => setClassOfLoss(v ?? "")}>
            <SelectTrigger id="le-class" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Class 1</SelectItem>
              <SelectItem value="2">Class 2</SelectItem>
              <SelectItem value="3">Class 3</SelectItem>
              <SelectItem value="4">Class 4 — specialty</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <ResultPanel title="Ballpark Range" stat={low && high ? `${fmtUsd(low)} – ${fmtUsd(high)}` : "—"} unit=""
        rows={[
          { label: "Base rate (per ft²)", value: `$${BASE_RATE_BY_CATEGORY[category].toFixed(2)}` },
          { label: "Class multiplier", value: `${CLASS_MULT[classOfLoss].toFixed(2)}×` },
          { label: "Midpoint", value: fmtUsd(Math.round((low + high) / 2)) },
        ]}
        footnote="NOT A QUOTE. Order-of-magnitude range only. Reconstruction, lead remediation, and pack-out are additive." />
    </div>
  );
}

const INSULATION_FACTORS: Record<string, number> = { poor: 1.5, average: 1.0, good: 0.7 };
function HeatingBtuCalc() {
  const { settings } = useCalcSettings();
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState(String(settings.defaultHeightFt || 8));
  const [currentF, setCurrentF] = useState("");
  const [desiredF, setDesiredF] = useState("");
  const [insulation, setInsulation] = useState<"poor" | "average" | "good">("average");
  const r = useMemo(() => {
    const L = toNum(length), W = toNum(width), H = toNum(height);
    const volume = L * W * H;
    const dT = Math.abs(toNum(desiredF) - toNum(currentF));
    const insF = INSULATION_FACTORS[insulation];
    const btuPerHour = volume * dT * 0.24 * insF;
    return { volume, dT, btuPerHour, tons: btuPerHour / 12000, watts: btuPerHour / 3.412 };
  }, [length, width, height, currentF, desiredF, insulation]);
  const cFromF = (v: string) => v ? fmt(((toNum(v) - 32) * 5) / 9, 1) : "";
  const setFromC = (setter: (s: string) => void) => (c: string) => { if (!c) return setter(""); setter(fmt((toNum(c) * 9) / 5 + 32, 1)); };
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-3 gap-3">
          <NumField id="bt-l" label="Length (ft)" value={length} onChange={setLength} placeholder="24" />
          <NumField id="bt-w" label="Width (ft)" value={width} onChange={setWidth} placeholder="18" />
          <NumField id="bt-h" label="Height (ft)" value={height} onChange={setHeight} placeholder="8" />
        </div>
        <div>
          <Label style={{ color: "rgba(255,255,255,0.85)", marginBottom: 6, display: "block" }}>Insulation quality</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {(["poor", "average", "good"] as const).map((k) => (
              <button key={k} type="button" onClick={() => setInsulation(k)} style={{ padding: "10px 8px", borderRadius: 8, border: "1px solid " + (insulation === k ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: insulation === k ? "#B88A2F" : "transparent", color: insulation === k ? "#080F1A" : "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: 13, cursor: "pointer", textTransform: "capitalize" }}>{k}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField id="bt-cur-f" label="Current (°F)" value={currentF} onChange={setCurrentF} placeholder="78" />
          <NumField id="bt-cur-c" label="Current (°C)" value={cFromF(currentF)} onChange={setFromC(setCurrentF)} placeholder="25" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField id="bt-des-f" label="Desired (°F)" value={desiredF} onChange={setDesiredF} placeholder="72" />
          <NumField id="bt-des-c" label="Desired (°C)" value={cFromF(desiredF)} onChange={setFromC(setDesiredF)} placeholder="22" />
        </div>
      </div>
      <div style={{ background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <BigStat label="BTU / hr" stat={r.btuPerHour ? fmt(r.btuPerHour) : "—"} highlight />
          <BigStat label="Tons HVAC" stat={r.tons ? fmt(r.tons, 2) : "—"} />
        </div>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <BigStat label="Watts" stat={r.watts ? fmt(r.watts) : "—"} />
          <BigStat label="ΔT" stat={r.dT ? `${fmt(r.dT, 1)} °F` : "—"} />
        </div>
        <div style={{ padding: "0 20px 20px" }}>
          <EmailButton summary={{ calcLabel: "Heating / Cooling BTU", rows: [{ label: "BTU / hr", value: fmt(r.btuPerHour) }, { label: "Tons HVAC", value: fmt(r.tons, 2) }] }} />
        </div>
      </div>
    </div>
  );
}

/* ── Asbestos calc — kept but never rendered (service line hidden) ── */
/* eslint-disable @typescript-eslint/no-unused-vars */
function _AsbestosCalcHidden() {
  return null;
}
/* eslint-enable @typescript-eslint/no-unused-vars */

const statStyle: React.CSSProperties = { color: "#ffffff", fontWeight: 600, fontVariantNumeric: "tabular-nums" };

/* -------------------------------------------------------------------------- */
/*  Field Reference Guide                                                      */
/* -------------------------------------------------------------------------- */
function ReferenceCard({ id, open, onToggle, title, preview, children }: { id: string; open: boolean; onToggle: () => void; title: string; preview: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, overflow: "hidden" }}>
      <button type="button" onClick={onToggle} style={{ width: "100%", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer", background: "transparent", border: "none", textAlign: "left", gap: 16 }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", lineHeight: 1.4 }}>{title}</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.60)", marginTop: 4, lineHeight: 1.5 }}>{preview}</p>
        </div>
        <span style={{ flexShrink: 0, marginTop: 2 }}>
          {open ? <ChevronDown className="size-4" style={{ color: "#D4A84B" }} /> : <ChevronRight className="size-4" style={{ color: "#D4A84B" }} />}
        </span>
      </button>
      {open && <div style={{ padding: "4px 20px 20px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>{children}</div>}
    </div>
  );
}

function RefTable({ rows }: { rows: { label: string; detail: string; extra?: string }[] }) {
  return (
    <div style={{ marginTop: 14, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.18)" }}>
      {rows.map((row, i) => (
        <div key={i} className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:items-start sm:gap-3" style={{ padding: "11px 14px", borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none", background: i % 2 === 0 ? "rgba(255,255,255,0.04)" : "transparent" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", lineHeight: 1.45, marginBottom: 4 }}>{row.label}</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.82)", lineHeight: 1.55 }}>{row.detail}{row.extra && <span style={{ display: "block", marginTop: 3, color: "rgba(255,255,255,0.55)", fontSize: 11 }}>{row.extra}</span>}</span>
        </div>
      ))}
    </div>
  );
}

function RefParagraph({ children }: { children: React.ReactNode }) {
  return <p style={{ marginTop: 12, fontSize: 13, color: "rgba(255,255,255,0.78)", lineHeight: 1.65 }}>{children}</p>;
}

function FieldReferenceGuide() {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  return (
    <section style={{ padding: "0 24px 48px", position: "relative", zIndex: 1 }}>
      <AnimateOnScroll>
        <div className="p-5 sm:p-8 lg:p-10" style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.85)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.15)", borderTop: "3px solid #B88A2F", boxShadow: "0 4px 32px rgba(0,0,0,0.5)" }}>
          <div className="flex items-start gap-3 mb-2">
            <BookOpen className="size-5 mt-1 shrink-0" style={{ color: "#D4A84B" }} />
            <div>
              <h2 className="text-2xl font-bold" style={{ color: "#ffffff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Field Reference Guide</h2>
              <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.6 }}>Not a restoration professional? Start here. Click any topic to learn what it means and why it matters before entering numbers into the calculators.</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-6">
            <ReferenceCard id="water-class" open={!!open["water-class"]} onToggle={() => toggle("water-class")}
              title="Water Loss Classes — What Class 1, 2, 3, and 4 Mean (IICRC S500)"
              preview="Class tells you how deeply water has soaked into building materials. It drives how many air movers and dehumidifiers you need.">
              <RefParagraph>The IICRC S500 standard classifies water damage by how much moisture has been absorbed into building materials — not by how much water is visible on the floor.</RefParagraph>
              <RefTable rows={[
                { label: "Class 1 — Least absorbed", detail: "Only a portion of a room is affected. Affected materials have low porosity (concrete, tile, vinyl). Drying factor: 0.10 pints per cubic foot per day.", extra: "Example: A toilet supply line broke but was caught within an hour. Tile floor only, no carpet, no wall penetration." },
                { label: "Class 2 — Significant absorption", detail: "An entire room of carpet and pad is wet. Walls are wet up to 24 inches. Drying factor: 0.20 pints per cubic foot per day.", extra: "Example: A dishwasher leak ran overnight and soaked the kitchen carpet and base of walls." },
                { label: "Class 3 — Greatest absorption", detail: "Water came from above — ceiling, walls, insulation, carpet, subfloor all saturated. Drying factor: 0.30 pints per cubic foot per day.", extra: "Example: An upstairs bathroom overflowed through the subfloor into the ceiling below." },
                { label: "Class 4 — Specialty drying", detail: "Very low porosity materials: hardwood floors, plaster, concrete, stone, brick. Drying factor: 0.40 pints per cubic foot per day.", extra: "Example: Hardwood floors after a flood — requires specialized drying mats and extended drying time." },
              ]} />
            </ReferenceCard>
            <ReferenceCard id="water-cat" open={!!open["water-cat"]} onToggle={() => toggle("water-cat")}
              title="Water Categories — What Category 1, 2, and 3 Means (IICRC S500)"
              preview="Category tells you how contaminated the water is. It determines safety protocols, whether materials can be dried or must be removed, and the cost of the job.">
              <RefParagraph>The IICRC S500 standard categorizes water by its contamination level at the time of contact. Category is separate from Class — both must be assessed independently.</RefParagraph>
              <RefTable rows={[
                { label: "Category 1 — Clean water", detail: "Water from a sanitary source that poses no substantial health risk. Supply line breaks, rain intrusion, overflow from clean toilet tanks.", extra: "Materials can usually be dried in place if addressed quickly. Degrades to Category 2 within 24–48 hours if left standing." },
                { label: "Category 2 — Gray water", detail: "Water containing significant contamination. Dishwasher/washing machine overflow, aquarium leak, toilet bowl overflow (urine only).", extra: "Porous materials in contact typically must be removed. PPE required." },
                { label: "Category 3 — Black water", detail: "Grossly contaminated water: sewage backup, toilet overflow with feces, rising flood/river water.", extra: "All porous materials must be demolished. Full PPE, containment, and HEPA filtration required." },
              ]} />
            </ReferenceCard>
            <ReferenceCard id="ach" open={!!open["ach"]} onToggle={() => toggle("ach")}
              title="Air Changes Per Hour (ACH) — Targets by Trade and Containment Type"
              preview="ACH measures how many times per hour all the air in a contained space is filtered through a HEPA machine. Higher ACH = more protection.">
              <RefTable rows={[
                { label: "Water restoration (S500)", detail: "No ACH minimum. Equipment placement focuses on maximizing drying airflow, not filtration.", extra: "The psychrometric calculator matters more here than ACH." },
                { label: "Mold remediation (S520)", detail: "4 ACH for Level I/II. 6 ACH for Level III. 8 ACH for Level IV. 12 ACH for critical environments.", extra: "Containment must maintain negative pressure −0.02 to −0.05 inches WC." },
                { label: "Fire / smoke restoration", detail: "4–6 ACH for HEPA air scrubbing and odor control.", extra: "" },
                { label: "Healthcare / critical environments", detail: "12–15 ACH for standard patient rooms. 15+ ACH for isolation rooms.", extra: "Requires written ICRA approval and commissioning air test before re-occupancy." },
              ]} />
            </ReferenceCard>
            {/* Asbestos & NESHAP reference card temporarily hidden alongside the asbestos service line */}
            <ReferenceCard id="dehu-selection" open={!!open["dehu-selection"]} onToggle={() => toggle("dehu-selection")}
              title="Portable LGR vs. Desiccant Dehumidifiers — When to Use Each"
              preview="Standard portable dehus stop working when it gets cold or when the air is already very dry. Desiccant units work in any condition.">
              <RefTable rows={[
                { label: "Temperature below 45°F", detail: "Any loss in an unheated garage, warehouse, cold-storage facility, or winter building. Portables ice up and stop working." },
                { label: "Large commercial losses", detail: "Any single loss footprint over 20,000–30,000 sq ft. A desiccant trailer handles the whole structure from a single connection." },
                { label: "Whole-building HVAC drying", detail: "When ducts, air handlers, and HVAC components are wet, a desiccant unit connects to the existing duct system." },
                { label: "RH already below 35%", detail: "In Arizona summer conditions (5–15% outdoor RH), desiccant units push indoor RH below 20% to pull moisture from deep materials." },
                { label: "Portable count > 20 units", detail: "When the math says more than 20 portable units, a desiccant trailer is the more practical and often cheaper option." },
              ]} />
            </ReferenceCard>
            <ReferenceCard id="power" open={!!open["power"]} onToggle={() => toggle("power")}
              title="Why Power & Amperage Matters When Running Drying Equipment"
              preview="Tripping a breaker at 2 AM stops all drying. Eight hours of no equipment = moisture re-absorption into materials.">
              <RefParagraph>Every air mover, dehumidifier, and negative-air machine draws current from the building's electrical circuits. Exceeding circuit capacity trips the breaker. The NEC requires that continuous loads not exceed 80% of a circuit's rated capacity.</RefParagraph>
              <RefTable rows={[
                { label: "Air mover (centrifugal)", detail: "1.3–3.0 amps at 115V. A 20A circuit can run 5–8 air movers if no dehu is on the same circuit." },
                { label: "LGR dehu (small)", detail: "4–7 amps at 115V. One LGR dehu + 2–3 air movers typically maxes a 20A circuit." },
                { label: "LGR dehu (large)", detail: "8–15 amps at 115V. Large units should be on their own dedicated 20A circuit." },
                { label: "HEPA NAM (500 CFM)", detail: "4–7 amps at 115V. Similar load to a mid-size dehu." },
              ]} />
            </ReferenceCard>
          </div>
        </div>
      </AnimateOnScroll>
    </section>
  );
}
